import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HeartIcon, RotateCcwIcon } from 'lucide-react';
import { SwipeDeck } from '../swipe/SwipeDeck';
import { PerformerCardFace } from '../swipe/PerformerCardFace';
import { Button } from '../ui/Button';
import { performers } from '../../data/performers';
import { events } from '../../data/events';
import { organiserPrefs } from '../../data/conversations';
import { useLanguage } from '../../contexts/LanguageContext';
import { travelCost } from '../../utils/event';
import { performerFit } from '../../utils/fit';
import { rise, spring, stagger } from '../../utils/motion';
import type { Performer } from '../../types/marketplace';
const demoEvent = events[0];
const demoPerformers = ['p3', 'p6', 'p7', 'p4', 'p2'].map(id => performers.find(p => p.id === id) as Performer);
export function DemoDeck() {
  const {
    t
  } = useLanguage();
  const [deck, setDeck] = useState(demoPerformers);
  const [liked, setLiked] = useState<Performer[]>([]);
  const [passed, setPassed] = useState<Performer[]>([]);
  const matched = liked.length >= 2;
  const reset = () => {
    setDeck(demoPerformers);
    setLiked([]);
    setPassed([]);
  };
  return <section aria-labelledby="demo-title" className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 lg:grid-cols-[1fr_1.05fr] lg:py-28" data-id="element-306" data-mp="30veb85kjob6f">
      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{
      once: true,
      margin: '-15% 0px'
    }} data-id="element-307" data-mp="285ffjrbuhfss">
        <motion.h2 variants={rise} id="demo-title" className="text-[36px] font-bold leading-[1.05] tracking-[-0.015em] lg:text-[56px]" data-id="element-308" data-mp="2d40g5rv0e1hr">
          {t('demo.title')}
        </motion.h2>
        <p className="mt-5 max-w-[44ch] text-[17px] leading-relaxed text-ink-2" data-id="element-309" data-mp="2m4ym4fp0cj2z">
          This is how organisers review acts for <span className="font-bold text-ink" data-id="element-310" data-mp="3sm2835f6quea">{demoEvent.name}</span>. Drag a card, tap a play button to hear a sample, or use the buttons.
        </p>
        <div className="mt-8 flex items-center gap-3" aria-live="polite" data-id="element-311" data-mp="1h2epk6qrljc">
          {[0, 1].map(i => <motion.span key={i} animate={{
          scale: liked.length > i ? 1 : 0.85
        }} transition={spring.bouncy} className={`grid h-12 w-12 place-items-center rounded-full ${liked.length > i ? 'bg-accent text-on-accent' : 'border-2 border-dashed border-line text-ink-3'}`} data-id="element-312" data-mp="1xjyutcddn4ka">
              <HeartIcon className="h-5 w-5" fill={liked.length > i ? 'currentColor' : 'none'} data-id="element-313" data-mp="1equyfj8rydlz" />
            </motion.span>)}
          <span className="text-[15px] font-semibold text-ink-2" data-id="element-314" data-mp="19ucv8nt3enbr">
            {matched ? 'Two matches — chats unlocked.' : `${2 - liked.length} to go`}
          </span>
        </div>
      </motion.div>

      <div className="relative mx-auto h-[600px] w-full max-w-[400px]" data-id="element-315" data-mp="1jpej7gbmtjcr">
        <AnimatePresence mode="wait" data-id="element-316" data-mp="w03h9vijpix1">
          {matched ? <motion.div key="match" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={spring.bouncy} className="flex h-full flex-col items-center justify-center rounded-lg bg-ink px-8 text-center text-bg" data-id="element-317" data-mp="r2bnsx295oqq">
              <div className="flex" data-id="element-318" data-mp="2tmmgc8x9qdua">
                {liked.slice(0, 2).map((p, i) => <motion.img key={p.id} src={p.photos[0]} alt={p.name} className="h-28 w-28 rounded-full border-4 border-ink object-cover" initial={{
              x: i === 0 ? -60 : 60,
              rotate: i === 0 ? -16 : 16,
              opacity: 0
            }} animate={{
              x: i === 0 ? 10 : -10,
              rotate: i === 0 ? -5 : 5,
              opacity: 1
            }} transition={spring.bouncy} data-id="element-319" data-mp="2xqkh0o28rwpg" data-mpi={i} />)}
              </div>
              <p className="mt-6 text-4xl font-bold tracking-[-0.02em]" data-id="element-320" data-mp="212efia0m9kfe">It’s a match</p>
              <p className="mt-2 max-w-[28ch] text-[15px] opacity-80" data-id="element-321" data-mp="25x1t3du30uf7">
                {liked[0].name} and {liked[1].name} said yes to {demoEvent.name}. Next: chat and send offers.
              </p>
              <Button variant="accent" className="mt-8" icon={<RotateCcwIcon className="h-4 w-4" data-id="element-323" data-mp="2bi6ut12fc260" />} onClick={reset} data-id="element-322" data-mp="2gfykrpjide7k">
                Play again
              </Button>
            </motion.div> : <motion.div key="deck" className="h-full" exit={{
          opacity: 0,
          scale: 0.96,
          transition: {
            duration: 0.15
          }
        }} data-id="element-324" data-mp="27fjxwlqwbr1y">
              <SwipeDeck items={deck} getKey={p => p.id} renderCard={p => <PerformerCardFace performer={p} fit={performerFit(p, demoEvent, organiserPrefs)} travel={travelCost(p, p.distanceKm)} data-id="element-326" data-mp="2v5wh4srlda7u" />} onSwipe={(p, dir) => {
            setDeck(d => d.filter(x => x.id !== p.id));
            if (dir === 'right') setLiked(l => [...l, p]);else setPassed(l => [...l, p]);
          }} stamps={{
            right: 'Match',
            left: 'Pass'
          }} likeLabel="Match" likeIcon={<HeartIcon className="h-7 w-7" fill="currentColor" data-id="element-327" data-mp="3vg75alzvp2fw" />} canUndo={passed.length > 0} onUndo={() => {
            const last = passed[passed.length - 1];
            setPassed(l => l.slice(0, -1));
            setDeck(d => [last, ...d]);
          }} empty={<div className="grid h-full place-items-center rounded-lg border-2 border-dashed border-line" data-id="element-328" data-mp="d3ixa87oyqu2">
                    <Button variant="primary" onClick={reset} data-id="element-329" data-mp="mzlwp6b5sbdb">
                      Shuffle the deck
                    </Button>
                  </div>} data-id="element-325" data-mp="2z8srxxaw4218" />
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}